import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-webapi/commerce-webapi/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "storeconfig-query"
    }}>{`storeConfig query`}</h1>
    <p>{`The `}<inlineCode parentName="p">{`storeConfig`}</inlineCode>{` query defines information about a store's configuration. You can query a non-default store by `}<a parentName="p" {...{
        "href": "../../../usage/headers.md"
      }}>{`changing the header`}</a>{` in your GraphQL request.`}</p>
    <h2 {...{
      "id": "syntax"
    }}>{`Syntax`}</h2>
    <p><inlineCode parentName="p">{`storeConfig: StoreConfig`}</inlineCode></p>
    <h2 {...{
      "id": "reference"
    }}>{`Reference`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/webapi/graphql-api/index.html#query-storeConfig"
      }}><inlineCode parentName="a">{`storeConfig`}</inlineCode></a>{` reference provides detailed information about the types and fields defined in this query.`}</p>
    <h2 {...{
      "id": "example-usage"
    }}>{`Example usage`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/developer/commerce/storefront/playgrounds/commerce-services/"
      }}>{`Commerce API playground`}</a>{` provides a sample `}<inlineCode parentName="p">{`storeConfig`}</inlineCode>{` query that you can run against a live instance of Adobe Commerce with Luma sample data. Note that the responses may vary, depending on the configuration of the Commerce instance.`}</p>
    <h3 {...{
      "id": "query-a-stores-configuration"
    }}>{`Query a store's configuration`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`storeConfig`}</inlineCode>{` query can return `}<inlineCode parentName="p">{`base`}</inlineCode>{` and `}<inlineCode parentName="p">{`extended`}</inlineCode>{` store configuration setting. The following call returns all `}<inlineCode parentName="p">{`base`}</inlineCode>{` details of a store's configuration.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`{
  storeConfig {
    store_code
    store_name
    is_default_store
    store_group_code
    is_default_store_group
    locale
    base_currency_code
    default_display_currency_code
    timezone
    weight_unit
    base_url
    base_link_url
    base_static_url
    base_media_url
    secure_base_url
    secure_base_link_url
    secure_base_static_url
    secure_base_media_url
  }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "storeConfig": {
      "store_code": "default",
      "store_name": "Default Store View",
      "is_default_store": true,
      "store_group_code": "main_website_store",
      "is_default_store_group": true,
      "locale": "en_US",
      "base_currency_code": "USD",
      "default_display_currency_code": "USD",
      "timezone": "America/Chicago",
      "weight_unit": "lbs",
      "base_url": "http://example.com/",
      "base_link_url": "http://example.com/",
      "base_static_url": "http://example.com/pub/static/version1606976517/",
      "base_media_url": "http://example.com/pub/media/",
      "secure_base_url": "http://example.com/",
      "secure_base_link_url": "http://example.com/",
      "secure_base_static_url": "http://example.com/pub/static/version1606976517/",
      "secure_base_media_url": "http://example.com/pub/media/"
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "query-a-stores-theme"
    }}>{`Query a store's theme`}</h3>
    <p>{`The following query returns information about the store's default title, keywords, and welcome text.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`{
  storeConfig {
    default_title
    default_keywords
    welcome
  }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "storeConfig": {
      "default_title": "Adobe Commerce",
      "default_keywords": "Varien, E-commerce",
      "welcome": "Default welcome msg!"
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "query-a-stores-cms-configuration"
    }}>{`Query a store's CMS configuration`}</h3>
    <p>{`The following query returns information about the store's content pages.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`{
  storeConfig {
    front
    cms_home_page
    no_route
    cms_no_route
    cms_no_cookies
    show_cms_breadcrumbs
  }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "storeConfig": {
      "front": "cms",
      "cms_home_page": "home",
      "no_route": "cms/noroute/index",
      "cms_no_route": "no-route",
      "cms_no_cookies": "enable-cookies",
      "show_cms_breadcrumbs": 1
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "query-a-stores-catalog-configuration"
    }}>{`Query a store's Catalog configuration`}</h3>
    <p>{`The following query returns information about the store's catalog configuration.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`{
  storeConfig {
    product_url_suffix
    category_url_suffix
    title_separator
    list_mode
    grid_per_page_values
    list_per_page_values
    grid_per_page
    list_per_page
    catalog_default_sort_by
  }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "storeConfig": {
      "product_url_suffix": ".html",
      "category_url_suffix": ".html",
      "title_separator": "-",
      "list_mode": "grid-list",
      "grid_per_page_values": "9,15,30",
      "list_per_page_values": "5,10,15,20,25",
      "grid_per_page": 9,
      "list_per_page": 10,
      "catalog_default_sort_by": "position"
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "query-a-stores-customer-configuration"
    }}>{`Query a store's customer configuration`}</h3>
    <p>{`The following query returns information about the store's customer configuration.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`{
    storeConfig {
        autocomplete_on_storefront
        minimum_password_length
        required_character_classes_number
        create_account_confirmation
    }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "storeConfig": {
      "autocomplete_on_storefront": true,
      "minimum_password_length": "6",
      "required_character_classes_number": "2",
      "create_account_confirmation": false
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "query-a-stores-access-token-expiration-configuration"
    }}>{`Query a store's access token expiration configuration`}</h3>
    <p>{`The following query returns the value of the `}<strong parentName="p">{`Stores`}</strong>{` > Settings > `}<strong parentName="p">{`Configuration`}</strong>{` > `}<strong parentName="p">{`Services`}</strong>{` > `}<strong parentName="p">{`OAuth`}</strong>{` > `}<strong parentName="p">{`Access Token Expiration`}</strong>{` > `}<strong parentName="p">{`Customer Token Lifetime (hours)`}</strong>{` field.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`{
    storeConfig {
        customer_access_token_lifetime
    }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "storeConfig": {
      "customer_access_token_lifetime": 1
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "query-a-stores-fixed-product-tax-configuration"
    }}>{`Query a store's fixed product tax configuration`}</h3>
    <p>{`The following query returns enumeration values that indicate the store's fixed product tax configuration.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`{
  storeConfig {
    category_fixed_product_tax_display_setting
    product_fixed_product_tax_display_setting
    sales_fixed_product_tax_display_setting
  }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "storeConfig": {
      "category_fixed_product_tax_display_setting": "EXCLUDE_FPT_WITHOUT_DETAILS",
      "product_fixed_product_tax_display_setting": "EXCLUDE_FPT_AND_INCLUDE_WITH_DETAILS",
      "sales_fixed_product_tax_display_setting": "INCLUDE_FPT_WITHOUT_DETAILS"
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "query-a-stores-order-cancellation-configuration"
    }}>{`Query a store's order cancellation configuration`}</h3>
    <p>{`The following query returns the store's order cancellation settings, indicating if it is enabled and the available cancellation reasons.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{` {
    storeConfig {
        order_cancellation_enabled
        order_cancellation_reasons {
            description
        }
    }
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "data": {
    "storeConfig": {
      "order_cancellation_enabled": true,
      "order_cancellation_reasons": [
        {
          "description": "The item(s) are no longer needed"
        },
        {
          "description": "The order was placed by mistake"
        },
        {
          "description": "Item(s) not arriving within the expected timeframe"
        },
        {
          "description": "Found a better price elsewhere"
        },
        {
          "description": "Other"
        }
      ]
    }
  }
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      